// `g`-prefix shortcuts (workspace-modes note §4): `g` then a letter, outside editors.
// Shift+digit types punctuation in a composer and ⌘1–9 belongs to browser tabs, so neither is
// used. The registry is the mode list, so ⌘K and the help sheet show the same keys.
import { useEffect, useRef } from "react";

export const CHORD_TIMEOUT_MS = 1500;

export type GoShortcut = { key: string; label: string; run: () => void };

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/** Binds `g <key>` on `window`; disabled while `enabled` is false (e.g. presenting). */
export function useGoShortcuts(shortcuts: readonly GoShortcut[], enabled = true): void {
  const latest = useRef(shortcuts);
  latest.current = shortcuts;

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    let armedUntil = 0;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditable(event.target)) return;
      const now = Date.now();
      if (armedUntil > now) {
        armedUntil = 0;
        const hit = latest.current.find((s) => s.key === event.key);
        if (hit) {
          event.preventDefault();
          hit.run();
        }
        return;
      }
      if (event.key === "g" && !event.shiftKey) armedUntil = now + CHORD_TIMEOUT_MS;
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
